import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { notifier } from 'components/Toaster';
import useGetOneExamType from 'hooks/API/GET/useGetOneExamType';
import useGetExamBoards from 'hooks/API/GET/useGetExamBoards';
import useGetAllExamTypes from 'hooks/API/GET/useGetAllExamTypes';
import usePodcastDropdown from 'hooks/API/GET/usePodcastDropdown';
import {
  useManageExamType,
  useSaveMarkDistribution,
  useSaveSyllabus,
  useFetchExamTypeForCopy,
} from 'hooks/API/POST/useManageExamInfo';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import PodcastDropdown from 'components/molecules/PodcastDropdown';
import MarkDistributionEditor from './MarkDistributionEditor';
import SyllabusEditor from './SyllabusEditor';
import * as AppPics from 'assets/images';

const { AppTextField, AppSelectBox, AppButton } = require('components');

// ── Date helpers ──────────────────────────────────────────────────────────────
const toInputDate = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');

const EMPTY_BASIC = {
  name: '',
  status: 'active',
  categoryNumber: '',
  vacancy: '',
  salaryScale: '',
  ageQualification: '',
  selectionProcess: '',
  cutOffMarks: '',
};

const EMPTY_DATES = {
  notificationDate: '',
  lastApplyDate: '',
  examDate: '',
  admitCardDate: '',
};

const extractId = (v) => v?._id?.toString() || v?.toString() || '';

// Maps API syllabus rows into editor state. `keepIds: false` drops each section's _id —
// used when loading a *different* exam type's syllabus as a copy template, so saving
// creates new sections here instead of trying to update the source exam type's rows.
const buildSyllabusRows = (syllabus, { keepIds = true } = {}) =>
  (syllabus || []).map((s) => {
    let subSections;
    if (s.subSections && s.subSections.length > 0) {
      // New 3-level format
      subSections = s.subSections.map((sub) => ({
        heading: sub.heading || '',
        content: sub.content || '',
        podcastId: extractId(sub.podcastId),
        items: (sub.items || []).map((item) => ({
          label: item.label || '',
          content: item.content || '',
          episodeId: extractId(item.episodeId),
        })),
      }));
    } else if (s.podcastId || (s.items && s.items.length > 0)) {
      // Old 2-level format — wrap existing podcastId/items into a single sub-section
      subSections = [
        {
          heading: '',
          content: '',
          podcastId: extractId(s.podcastId),
          items: (s.items || []).map((item) => ({
            label: item.label || '',
            content: item.content || '',
            episodeId: extractId(item.episodeId),
          })),
        },
      ];
    } else {
      subSections = [];
    }
    return {
      _id: keepIds ? s._id || null : null,
      heading: s.heading,
      content: s.content || '',
      subSections,
    };
  });

// Maps API mark-distribution rows into editor state. `keepIds: false` drops each
// row's _id (parent and child) — same reasoning as buildSyllabusRows: the backend's
// save upserts by `{_id, examTypeId}`, so a foreign _id would silently match nothing
// instead of creating a new row.
const buildMarkRows = (markDistribution, { keepIds = true } = {}) =>
  (markDistribution || []).map((m) => ({
    _id: keepIds ? m._id || null : null,
    topic: m.topic,
    marks: m.marks ?? '',
    podcastId: extractId(m.podcastId),
    children: (m.children || []).map((c) => ({
      _id: keepIds ? c._id || null : null,
      topic: c.topic,
      marks: c.marks ?? '',
      podcastId: extractId(c.podcastId),
    })),
  }));

export default function ManageExamType() {
  const { boardId, examTypeId } = useParams();
  const history = useHistory();

  // ── Data fetching ──────────────────────────────────────────────────────────
  const { data: etData, isLoading } = useGetOneExamType({ examTypeId, enabled: !!examTypeId });
  const { data: boardsData } = useGetExamBoards({ enabled: true });
  const boards = boardsData?.data || [];
  const board = boards.find((b) => b._id === boardId);

  // Podcast dropdown — uses ?mode=dropdown → [{label, value}]
  const {
    data: podcastDropdownData,
    isLoading: podcastsLoading,
    isError: podcastsError,
  } = usePodcastDropdown({ enabled: true, includeUnpublished: true });

  // Response chain: axios response → .data (body) → { status, data: [{label,value}] } → .data
  const podcastOptions = podcastDropdownData?.data?.data || [];

  // ── Local state ────────────────────────────────────────────────────────────
  const [basic, setBasic] = useState({ ...EMPTY_BASIC });
  const [dates, setDates] = useState({ ...EMPTY_DATES });
  const [pyqPodcastId, setPyqPodcastId] = useState('');
  const [markRows, setMarkRows] = useState([]);
  const [syllabusRows, setSyllabusRows] = useState([]);

  // ── Populate from API ──────────────────────────────────────────────────────
  useEffect(() => {
    if (etData?.data) {
      const et = etData.data;
      setBasic({
        name: et.name || '',
        status: et.status || 'active',
        categoryNumber: et.categoryNumber || '',
        vacancy: et.vacancy || '',
        salaryScale: et.salaryScale || '',
        ageQualification: et.ageQualification || '',
        selectionProcess: et.selectionProcess || '',
        cutOffMarks: et.cutOffMarks || '',
      });
      setDates({
        notificationDate: toInputDate(et.notificationDate),
        lastApplyDate: toInputDate(et.lastApplyDate),
        examDate: toInputDate(et.examDate),
        admitCardDate: toInputDate(et.admitCardDate),
      });
      setPyqPodcastId(et.pyqPodcastId?._id || et.pyqPodcastId || '');
      // Map hierarchical mark distribution from API — preserve _id for delete API calls
      // admin-mcs returns lean Mongoose docs: podcastId is a populated object { _id, name, ... }
      // NOT the podcast-mcs shape { podcast: { podcastId } }, so we read ._id directly.
      setMarkRows(buildMarkRows(et.markDistribution));
      setSyllabusRows(buildSyllabusRows(et.syllabus));
      // Fresh data just arrived from the server — whatever's on screen is real, saved content.
      setIsCopyPreview(false);
      setIsMarkCopyPreview(false);
    }
  }, [etData]);

  // ── Copy from another exam type (syllabus + mark distribution) ───────────────
  const [copySourceId, setCopySourceId] = useState('');
  const [copyMarkSourceId, setCopyMarkSourceId] = useState('');
  // True once a copy has been loaded but not yet saved — while true, switching the
  // dropdown again just swaps the preview instead of asking "replace?" each time,
  // since there's nothing saved to lose until "Save Syllabus"/"Save Mark Distribution"
  // is actually clicked.
  const [isCopyPreview, setIsCopyPreview] = useState(false);
  const [isMarkCopyPreview, setIsMarkCopyPreview] = useState(false);
  const { data: allExamTypesData } = useGetAllExamTypes({ enabled: true });
  const allExamTypes = allExamTypesData?.data || [];
  const copyOptions = allExamTypes.filter((et) => et._id !== examTypeId && et.hasSyllabus);
  const copyMarkOptions = allExamTypes.filter(
    (et) => et._id !== examTypeId && et.hasMarkDistribution
  );
  const { mutate: fetchCopySource, isLoading: isFetchingCopySource } = useFetchExamTypeForCopy();
  const { mutate: fetchMarkCopySource, isLoading: isFetchingMarkCopySource } =
    useFetchExamTypeForCopy();

  const handleCopySyllabus = () => {
    if (!copySourceId) return;
    if (
      syllabusRows.length > 0 &&
      !isCopyPreview &&
      !window.confirm('This will replace your current syllabus with the copied one. Continue?')
    ) {
      return;
    }
    fetchCopySource(copySourceId, {
      onSuccess: (res) => {
        setSyllabusRows(buildSyllabusRows(res?.data?.syllabus, { keepIds: false }));
        setIsCopyPreview(true);
        notifier.success('Syllabus copied — review and click Save Syllabus to keep it');
      },
      onError: () => notifier.error('Failed to load syllabus to copy'),
    });
  };

  const handleCopyMarkDistribution = () => {
    if (!copyMarkSourceId) return;
    if (
      markRows.length > 0 &&
      !isMarkCopyPreview &&
      !window.confirm(
        'This will replace your current mark distribution with the copied one. Continue?'
      )
    ) {
      return;
    }
    fetchMarkCopySource(copyMarkSourceId, {
      onSuccess: (res) => {
        setMarkRows(buildMarkRows(res?.data?.markDistribution, { keepIds: false }));
        setIsMarkCopyPreview(true);
        notifier.success('Mark distribution copied — review and click Save to keep it');
      },
      onError: () => notifier.error('Failed to load mark distribution to copy'),
    });
  };

  // ── Mutations ──────────────────────────────────────────────────────────────
  const { mutate: saveExamType, isLoading: savingBasic } = useManageExamType();
  const { mutate: saveMarks, isLoading: savingMarks } = useSaveMarkDistribution();
  const { mutate: saveSyllabus, isLoading: savingSyllabus } = useSaveSyllabus();

  const handleSaveBasic = () => {
    saveExamType(
      { boardId, examTypeId, ...basic, notify: false },
      {
        onSuccess: () => notifier.success('Basic details saved'),
        onError: () => notifier.error('Save failed'),
      }
    );
  };

  const handleSaveDates = (notify) => {
    saveExamType(
      { boardId, examTypeId, ...dates, pyqPodcastId: pyqPodcastId || null, notify },
      {
        onSuccess: () =>
          notifier.success(notify ? 'Dates saved & notification sent!' : 'Dates saved'),
        onError: () => notifier.error('Save failed'),
      }
    );
  };

  const handleSaveMarks = () => {
    saveMarks(
      { examTypeId, rows: markRows },
      {
        onSuccess: () => notifier.success('Mark distribution saved'),
        onError: () => notifier.error('Save failed'),
      }
    );
  };

  const handleSaveSyllabus = () => {
    const cleanRows = syllabusRows.map(({ _id, heading, content, subSections }) => ({
      // Preserve _id so the backend diff-merges instead of re-inserting unchanged sections
      ...(_id ? { _id } : {}),
      heading,
      content: content || '',
      subSections: (subSections || []).map(({ heading: sh, content: sc, podcastId, items }) => ({
        heading: sh || '',
        content: sc || '',
        podcastId: podcastId || null,
        items: (items || []).map(({ label, content: ic, episodeId }) => ({
          label,
          content: ic || '',
          episodeId: episodeId || null,
        })),
      })),
    }));
    saveSyllabus(
      { examTypeId, rows: cleanRows },
      {
        onSuccess: () => notifier.success('Syllabus saved'),
        onError: () => notifier.error('Save failed'),
      }
    );
  };

  const handleSavePyq = () => {
    saveExamType(
      { boardId, examTypeId, pyqPodcastId: pyqPodcastId || null, notify: false },
      {
        onSuccess: () => notifier.success('PYQ podcast saved'),
        onError: () => notifier.error('Save failed'),
      }
    );
  };

  if (isLoading) {
    return (
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <div className="tab__content ___adjust___tab___padding">
            <div className="tab__content-actBody px-5 py-5">
              <p>Loading exam type…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        {/* ── Top menu bar ─────────────────────────────────────────────────── */}
        <ul className="tab__menu">
          <li className="back">
            <button
              className="btn-back"
              onClick={() => history.push(`/podcast/exam-info/${boardId}/exam-types`)}
            >
              <img src={AppPics.BackSvg} alt="Back" />
            </button>
          </li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          {/* ── Breadcrumbs ───────────────────────────────────────────────── */}
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[
                { label: 'Dashboard', to: '/dashboard' },
                { label: 'Podcast', to: '/podcast' },
                { label: 'Exam Info', to: '/podcast/exam-info' },
                {
                  label: board?.name || 'Board',
                  to: `/podcast/exam-info/${boardId}/exam-types`,
                },
              ]}
              current={basic.name || 'Manage Exam Type'}
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              <div className="tab__contentMenu">
                <ul className="tab__contentMenu-list">
                  <li>
                    <h2 className="prime-head p-0 boldtitle text-nowrap">
                      {basic.name ? `Manage: ${basic.name}` : 'Manage Exam Type'}
                    </h2>
                  </li>
                </ul>
              </div>

              {/* ── Section 1: Basic Details ─────────────────────────────── */}
              <ExamSection title="1. Basic Details">
                <div className="row">
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Exam Type Name *' }}
                      inputProps={{
                        placeholder: 'e.g. Lower Division Clerk',
                        value: basic.name,
                        onChange: (e) => setBasic({ ...basic, name: e.target.value }),
                      }}
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-4">
                    <AppTextField
                      labelProps={{ label: 'Category Number' }}
                      inputProps={{
                        placeholder: 'e.g. 123/2024',
                        value: basic.categoryNumber,
                        onChange: (e) => setBasic({ ...basic, categoryNumber: e.target.value }),
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <AppTextField
                      labelProps={{ label: 'Total Vacancies' }}
                      inputProps={{
                        placeholder: 'e.g. 24',
                        value: basic.vacancy,
                        onChange: (e) => setBasic({ ...basic, vacancy: e.target.value }),
                      }}
                    />
                  </div>
                  <div className="col-md-4">
                    <AppTextField
                      labelProps={{ label: 'Cut Off Marks (Prev. Year)' }}
                      inputProps={{
                        placeholder: 'e.g. 75',
                        value: basic.cutOffMarks,
                        onChange: (e) => setBasic({ ...basic, cutOffMarks: e.target.value }),
                      }}
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Salary Scale' }}
                      inputProps={{
                        placeholder: 'e.g. ₹25,000 – ₹50,000/month',
                        value: basic.salaryScale,
                        onChange: (e) => setBasic({ ...basic, salaryScale: e.target.value }),
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <AppSelectBox
                      labelProps={{ label: 'Status' }}
                      selectProps={{
                        value: basic.status,
                        onChange: (e) => setBasic({ ...basic, status: e.target.value }),
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="coming_soon">Coming Soon</option>
                    </AppSelectBox>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Age & Qualification' }}
                      inputProps={{
                        placeholder: 'e.g. 18–36 years, Degree holder',
                        value: basic.ageQualification,
                        onChange: (e) => setBasic({ ...basic, ageQualification: e.target.value }),
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Selection Process' }}
                      inputProps={{
                        placeholder: 'e.g. OMR Written Test, Interview',
                        value: basic.selectionProcess,
                        onChange: (e) => setBasic({ ...basic, selectionProcess: e.target.value }),
                      }}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <AppButton
                    variantClass="btn-primary"
                    onClick={handleSaveBasic}
                    disabled={savingBasic}
                  >
                    {savingBasic ? 'Saving…' : 'Save Basic Details'}
                  </AppButton>
                </div>
              </ExamSection>

              {/* ── Section 2: Important Dates ───────────────────────────── */}
              <ExamSection title="2. Important Dates">
                <div className="row">
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Notification Date' }}
                      inputProps={{
                        type: 'date',
                        value: dates.notificationDate,
                        onChange: (e) => setDates({ ...dates, notificationDate: e.target.value }),
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Last Apply Date' }}
                      inputProps={{
                        type: 'date',
                        value: dates.lastApplyDate,
                        onChange: (e) => setDates({ ...dates, lastApplyDate: e.target.value }),
                      }}
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Exam Date' }}
                      inputProps={{
                        type: 'date',
                        value: dates.examDate,
                        onChange: (e) => setDates({ ...dates, examDate: e.target.value }),
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <AppTextField
                      labelProps={{ label: 'Admit Card Download Date' }}
                      inputProps={{
                        type: 'date',
                        value: dates.admitCardDate,
                        onChange: (e) => setDates({ ...dates, admitCardDate: e.target.value }),
                      }}
                    />
                  </div>
                </div>

                <p className="text-muted mt-3" style={{ fontSize: 12 }}>
                  "Save &amp; Notify" will send a push notification to all podcast app users about
                  the changed date(s).
                </p>

                <div className="d-flex align-items-center mt-3" style={{ gap: 12 }}>
                  <AppButton
                    variantClass="btn-default"
                    onClick={() => handleSaveDates(false)}
                    disabled={savingBasic}
                  >
                    Save Dates
                  </AppButton>
                  <AppButton
                    variantClass="btn-primary"
                    onClick={() => handleSaveDates(true)}
                    disabled={savingBasic}
                  >
                    {savingBasic ? 'Sending…' : '🔔 Save & Notify Users'}
                  </AppButton>
                </div>
              </ExamSection>

              {/* ── Section 3: Mark Distribution ─────────────────────────── */}
              <ExamSection title="3. Distribution of Marks">
                <div className="d-flex align-items-end mb-4" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 320 }}>
                    <AppSelectBox
                      labelProps={{ label: 'Copy mark distribution from an existing exam type' }}
                      selectProps={{
                        value: copyMarkSourceId,
                        onChange: (e) => setCopyMarkSourceId(e.target.value),
                      }}
                    >
                      <option value="">— Select exam type to copy from —</option>
                      {copyMarkOptions.map((et) => (
                        <option key={et._id} value={et._id}>
                          {et.boardId?.name ? `${et.boardId.name} — ${et.name}` : et.name}
                        </option>
                      ))}
                    </AppSelectBox>
                  </div>
                  <AppButton
                    variantClass="btn btn-plane p--tint"
                    onClick={handleCopyMarkDistribution}
                    disabled={!copyMarkSourceId || isFetchingMarkCopySource}
                    style={{
                      color: '#7e1f8c',
                      background: '#f6e4f9',
                      borderColor: '#7e1f8c',
                      maxWidth: 'none',
                      width: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isFetchingMarkCopySource ? 'Loading…' : 'Copy Mark Distribution'}
                  </AppButton>
                </div>
                <p className="text-muted mb-4" style={{ fontSize: 12, marginTop: -12 }}>
                  This only fills the form below as a starting point — nothing is saved until you
                  click "Save Mark Distribution".
                </p>
                <MarkDistributionEditor
                  examTypeId={examTypeId}
                  rows={markRows}
                  onChange={setMarkRows}
                  podcastOptions={podcastOptions}
                  podcastsLoading={podcastsLoading}
                  podcastsError={podcastsError}
                />
                <div className="mt-4">
                  <AppButton
                    variantClass="btn-primary"
                    onClick={handleSaveMarks}
                    disabled={savingMarks || markRows.length === 0}
                  >
                    {savingMarks ? 'Saving…' : 'Save Mark Distribution'}
                  </AppButton>
                  {markRows.length === 0 && (
                    <span className="text-muted ml-3" style={{ fontSize: 12 }}>
                      Add at least one topic before saving.
                    </span>
                  )}
                </div>
              </ExamSection>

              {/* ── Section 4: Detailed Syllabus ─────────────────────────── */}
              <ExamSection title="4. Detailed Syllabus">
                <div className="d-flex align-items-end mb-4" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 320 }}>
                    <AppSelectBox
                      labelProps={{ label: 'Copy syllabus from an existing exam type' }}
                      selectProps={{
                        value: copySourceId,
                        onChange: (e) => setCopySourceId(e.target.value),
                      }}
                    >
                      <option value="">— Select exam type to copy from —</option>
                      {copyOptions.map((et) => (
                        <option key={et._id} value={et._id}>
                          {et.boardId?.name ? `${et.boardId.name} — ${et.name}` : et.name}
                        </option>
                      ))}
                    </AppSelectBox>
                  </div>
                  <AppButton
                    variantClass="btn btn-plane p--tint"
                    onClick={handleCopySyllabus}
                    disabled={!copySourceId || isFetchingCopySource}
                    style={{
                      color: '#7e1f8c',
                      background: '#f6e4f9',
                      borderColor: '#7e1f8c',
                      maxWidth: 'none',
                      width: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isFetchingCopySource ? 'Loading…' : 'Copy Syllabus'}
                  </AppButton>
                </div>
                <p className="text-muted mb-4" style={{ fontSize: 12, marginTop: -12 }}>
                  This only fills the form below as a starting point — nothing is saved until you
                  click "Save Syllabus".
                </p>
                <SyllabusEditor
                  rows={syllabusRows}
                  onChange={setSyllabusRows}
                  podcastOptions={podcastOptions}
                  podcastsLoading={podcastsLoading}
                  podcastsError={podcastsError}
                  suggestedPodcastIds={[
                    // Collect all podcastIds linked in mark distribution (for smart suggestions)
                    ...markRows
                      .flatMap((m) => [m.podcastId, ...(m.children || []).map((c) => c.podcastId)])
                      .filter(Boolean),
                  ]}
                />
                <div className="mt-4">
                  <AppButton
                    variantClass="btn-primary"
                    onClick={handleSaveSyllabus}
                    disabled={savingSyllabus || syllabusRows.length === 0}
                  >
                    {savingSyllabus ? 'Saving…' : 'Save Syllabus'}
                  </AppButton>
                  {syllabusRows.length === 0 && (
                    <span className="text-muted ml-3" style={{ fontSize: 12 }}>
                      Add at least one section before saving.
                    </span>
                  )}
                </div>
              </ExamSection>

              {/* ── Section 5: PYQ Podcast ───────────────────────────────── */}
              <ExamSection title="5. Previous Year Questions (PYQ) Podcast">
                <div style={{ maxWidth: 480 }}>
                  <PodcastDropdown
                    version="v2"
                    label="Link Podcast"
                    value={pyqPodcastId}
                    options={podcastOptions}
                    isLoading={podcastsLoading}
                    isError={podcastsError}
                    placeholder="— Select PYQ podcast —"
                    handleChange={(e) => setPyqPodcastId(e.target.value)}
                  />
                  <p className="text-muted mt-2" style={{ fontSize: 12 }}>
                    Select the existing "Kerala PSC Solved Question Paper" podcast (or any PYQ
                    podcast you've created).
                  </p>
                  <div className="mt-3">
                    <AppButton variantClass="btn-primary" onClick={handleSavePyq}>
                      Save PYQ Link
                    </AppButton>
                  </div>
                </div>
              </ExamSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function ExamSection({ title, children }) {
  return (
    <div
      className="tab__contentBody mb-4"
      style={{
        background: '#fafafa',
        border: '1px solid #e8e8e8',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <h4
        style={{
          margin: '0 0 16px',
          fontWeight: 600,
          paddingBottom: 10,
          borderBottom: '1px solid #e8e8e8',
        }}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}
